import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { ManualCompanyForm } from './ManualCompanyForm'
import { FaIcon, faClose } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useQueryCompanyDatabases } from '@/lib/api/company/useCompanyDatabaseHooks'
import { useQueryClient } from '@tanstack/react-query'
import { invalidateAccessQueries } from '@/modules/shared/query-client'

// The creator job normally finishes within seconds; past this we stop pretending it is
// still coming and give the user a way out instead of a spinner with no exit.
const DB_INITIALIZATION_TIMEOUT_MS = 3 * 60 * 1000

interface ManualCompanyModalProps {
  isOpen: boolean
  close: () => void
  companyId: string
}

const ManualCompanyModal: React.FC<ManualCompanyModalProps> = ({
  isOpen,
  close,
  companyId,
}) => {
  const { t } = useLocalize()
  const loading = useLoadingStore(selectLoading)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [createdCompanyId, setCreatedCompanyId] = React.useState<string | null>(null)

  // The new company carries a fresh role for its creator, which the cached
  // account/permissions snapshot predates — refetch before we redirect into it.
  const handleCreated = async (id: string) => {
    setCreatedCompanyId(id)
    await invalidateAccessQueries(queryClient)
  }

  const [waitTimedOut, setWaitTimedOut] = React.useState(false)

  const { data: databases, isError } = useQueryCompanyDatabases(createdCompanyId, {
    waitForMigration: true,
  })

  const stuck = waitTimedOut || isError

  React.useEffect(() => {
    if (createdCompanyId === null) return undefined
    const timeout = setTimeout(
      () => setWaitTimedOut(true),
      DB_INITIALIZATION_TIMEOUT_MS,
    )
    return () => clearTimeout(timeout)
  }, [createdCompanyId])

  React.useEffect(() => {
    // Wait for the migration, not just for the database row: opening the dashboard while
    // the creator is still migrating throws NOT_MIGRATED_COMPANY (same as the .bak upload).
    if (
      createdCompanyId !== null &&
      databases &&
      databases.some((db) => db.isCompatibleWithModel === true)
    ) {
      navigate(`/et/company/${createdCompanyId}/dashboard`)
    }
  }, [databases, createdCompanyId, navigate])

  const handleClose = () => {
    setCreatedCompanyId(null)
    setWaitTimedOut(false)
    close()
  }

  if (!isOpen) return null

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[900px] tw-flex-col tw-rounded-lg tw-bg-white">
        {loading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-select-none tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}

        {/* Header */}
        <div className="tw-border-b tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <p className="tw-text-xl tw-font-semibold tw-text-content-base">
              {createdCompanyId !== null ? t('addCompany.dbInitialization') : t('addCompany.manualCreate')}
            </p>
            {(createdCompanyId === null || stuck) && (
              <FaIcon
                icon={faClose}
                className="tw-h-5 tw-w-5 tw-cursor-pointer"
                onClick={handleClose}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
          {createdCompanyId !== null ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-6 tw-px-4 tw-py-8">
              {stuck ? (
                <>
                  <p className="tw-text-center tw-text-base tw-text-content-base">
                    {t('addCompany.dbInitializingSlow')}
                  </p>
                  <div className="tw-flex tw-gap-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100"
                    >
                      {t('common.close')}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/et/company/${createdCompanyId}/dashboard`)}
                      className="tw-rounded-md tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90"
                    >
                      {t('addCompany.goToCompany')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <LoaderIcon className="tw-animate-spin tw-h-10 tw-w-10 tw-text-primary" />
                  <p className="tw-text-base tw-text-content-base">
                    {t('addCompany.dbInitializing')}
                  </p>
                </>
              )}
            </div>
          ) : (
            <ManualCompanyForm companyId={companyId} onSuccess={handleCreated} />
          )}
        </div>
      </div>
    </ModalLayout>
  )
}

export default ManualCompanyModal
